import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle, Clock, CreditCard, QrCode, RefreshCw, X, XCircle } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from './WithdrawalQueueScreen.module.css'

const REFRESH_INTERVAL_MS = 180_000

const TABS = [
    { key: 'pending', label: 'Pending' },
    { key: 'approved', label: 'Approved' },
    { key: 'processed', label: 'Processed' },
    { key: 'rejected', label: 'Rejected' },
]

const STATUS_BADGE_MAP = {
    submitted: { label: 'Submitted', cls: 'badgeSubmitted' },
    under_review: { label: 'Under Review', cls: 'badgeUnderReview' },
    approved: { label: 'Approved', cls: 'badgeApproved' },
    processed: { label: 'Processed', cls: 'badgeProcessed' },
    rejected: { label: 'Rejected', cls: 'badgeRejected' },
}

// ─── UTILS ───────────────────────────────────────────────────────────────────
function formatDateTime(dateStr) {
    if (!dateStr) return '—'
    const d = new Date(String(dateStr).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    })
}

function fmtAmount(amount) {
    if (amount == null) return '—'
    return `₹${Number(amount).toLocaleString('en-IN')}`
}

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
function StatusBadge({ status }) {
    const { label, cls } = STATUS_BADGE_MAP[status] || { label: status, cls: 'badgeSubmitted' }
    return <span className={`${styles.badge} ${styles[cls]}`}>{label}</span>
}

// ─── APPROVE MODAL ────────────────────────────────────────────────────────────
function ApproveModal({ withdrawal, onClose, onDone }) {
    const authCtx = useContext(AuthContext)
    const [upiName, setUpiName] = useState('')
    const [adminNotes, setAdminNotes] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const handleSubmit = async () => {
        setError('')
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralApproveWithdrawal', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    adminId: authCtx.userIdentifier,
                    withdrawalId: withdrawal.withdrawalId,
                    upiName,
                    adminNotes,
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                onDone(`Approved — ${fmtAmount(withdrawal.amount)} ready for transfer.`, false)
            } else {
                const errType = data.response?.errorType
                if (errType === 'NOT_APPROVABLE') {
                    onDone('Already processed by someone else. Refresh karo.', true)
                } else {
                    if (data.message === 'token Expired, please login again!') authCtx.logout()
                    else setError(data.message || 'Approve failed. Try again.')
                }
            }
        } catch {
            setError('Network error. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>Approve Withdrawal</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isSubmitting}><X size={13} /></button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.modalInfoChip}>
                        <CheckCircle size={13} color="#15803d" />
                        <span className={styles.modalInfoName}>{withdrawal.stylistName}</span>
                        <span className={styles.dot}>·</span>
                        <span className={styles.modalInfoAmount}>{fmtAmount(withdrawal.amount)}</span>
                        <span className={styles.dot}>·</span>
                        <span className={styles.modalInfoUpi}>{withdrawal.upiId}</span>
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>UPI Name (Beneficiary)</label>
                        <input
                            className={styles.formInput}
                            value={upiName}
                            onChange={e => setUpiName(e.target.value)}
                            placeholder="e.g. Anita Sharma"
                            disabled={isSubmitting}
                        />
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Admin Notes (internal)</label>
                        <input
                            className={styles.formInput}
                            value={adminNotes}
                            onChange={e => setAdminNotes(e.target.value)}
                            placeholder="Optional internal notes"
                            disabled={isSubmitting}
                        />
                    </div>
                    {error && <div className={styles.errorText}><AlertTriangle size={11} />{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button className={styles.btnSuccess} onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting
                            ? <><RefreshCw size={12} className={styles.spinIcon} /> Approving...</>
                            : <><CheckCircle size={12} /> Approve</>}
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose} disabled={isSubmitting}>Cancel</button>
                </div>
            </div>
        </div>
    )
}

// ─── REJECT MODAL ─────────────────────────────────────────────────────────────
function RejectModal({ withdrawal, onClose, onDone }) {
    const authCtx = useContext(AuthContext)
    const [rejectionReason, setRejectionReason] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const handleSubmit = async () => {
        if (!rejectionReason.trim()) return
        setError('')
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralRejectWithdrawal', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    adminId: authCtx.userIdentifier,
                    withdrawalId: withdrawal.withdrawalId,
                    rejectionReason: rejectionReason.trim(),
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                onDone(`Rejected. ${fmtAmount(withdrawal.amount)} ${withdrawal.stylistName} ke wallet mein wapas aa gaya.`, false)
            } else {
                const errType = data.response?.errorType
                if (errType === 'NOT_REJECTABLE') {
                    onDone('Already processed. Refresh karo.', true)
                } else if (errType === 'SERVER_ERROR') {
                    setError('Kuch problem aayi. Retry karo.')
                } else {
                    if (data.message === 'token Expired, please login again!') authCtx.logout()
                    else setError(data.message || 'Reject failed. Try again.')
                }
            }
        } catch {
            setError('Network error. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>Reject Withdrawal</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isSubmitting}><X size={13} /></button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.modalInfoChip}>
                        <XCircle size={13} color="#dc2626" />
                        <span className={styles.modalInfoName}>{withdrawal.stylistName}</span>
                        <span className={styles.dot}>·</span>
                        <span className={styles.modalInfoAmount}>{fmtAmount(withdrawal.amount)}</span>
                    </div>
                    <div className={styles.warningBanner}>
                        <AlertTriangle size={12} />
                        <span>{fmtAmount(withdrawal.amount)} wapas stylist ke wallet mein automatically credit ho jaayega.</span>
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>Reason (stylist ko dikhega)</label>
                        <textarea
                            className={styles.formTextarea}
                            value={rejectionReason}
                            onChange={e => setRejectionReason(e.target.value)}
                            placeholder="e.g. Invalid UPI ID — please recheck and resubmit."
                            disabled={isSubmitting}
                            rows={3}
                        />
                    </div>
                    {error && <div className={styles.errorText}><AlertTriangle size={11} />{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button
                        className={styles.btnDanger}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !rejectionReason.trim()}
                    >
                        {isSubmitting
                            ? <><RefreshCw size={12} className={styles.spinIcon} /> Rejecting...</>
                            : <><XCircle size={12} /> Reject & Reverse</>}
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose} disabled={isSubmitting}>Cancel</button>
                </div>
            </div>
        </div>
    )
}

// ─── PROCESS MODAL ────────────────────────────────────────────────────────────
function ProcessModal({ withdrawal, onClose, onDone }) {
    const authCtx = useContext(AuthContext)
    const [utr, setUtr] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [error, setError] = useState('')

    const isValid = /^\d{12}$/.test(utr)

    const handleSubmit = async () => {
        if (!isValid) return
        setError('')
        setIsSubmitting(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralMarkWithdrawalProcessed', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    adminId: authCtx.userIdentifier,
                    withdrawalId: withdrawal.withdrawalId,
                    upiTransactionRef: utr,
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                onDone(`Processed! UTR: ${utr}`, false)
            } else {
                const errType = data.response?.errorType
                if (errType === 'DUPLICATE_UTR') {
                    setError('Yeh UTR pehle se use ho chuka hai. Check karo.')
                } else if (errType === 'NOT_PROCESSABLE') {
                    onDone('Pehle approve karna hoga.', true)
                } else if (errType === 'INVALID_UTR') {
                    setError('Invalid UTR — 12 digits hone chahiye.')
                } else {
                    if (data.message === 'token Expired, please login again!') authCtx.logout()
                    else setError(data.message || 'Process failed. Try again.')
                }
            }
        } catch {
            setError('Network error. Please try again.')
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>Mark as Processed</span>
                    <button className={styles.iconBtn} onClick={onClose} disabled={isSubmitting}><X size={13} /></button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.modalInfoChip}>
                        <CreditCard size={13} color="#1d4ed8" />
                        <span className={styles.modalInfoName}>{withdrawal.stylistName}</span>
                        <span className={styles.dot}>·</span>
                        <span className={styles.modalInfoAmount}>{fmtAmount(withdrawal.amount)}</span>
                        <span className={styles.dot}>·</span>
                        <span className={styles.modalInfoUpi}>{withdrawal.upiId}</span>
                    </div>
                    <div className={styles.formField}>
                        <label className={styles.formLabel}>UPI Transaction Ref (UTR)</label>
                        <div className={styles.utrInputRow}>
                            <input
                                className={`${styles.formInput} ${utr.length > 0 && !isValid ? styles.formInputError : ''}`}
                                type="text"
                                inputMode="numeric"
                                maxLength={12}
                                value={utr}
                                onChange={e => {
                                    setError('')
                                    setUtr(e.target.value.replace(/\D/g, '').slice(0, 12))
                                }}
                                placeholder="123456789012"
                                disabled={isSubmitting}
                            />
                            <span className={`${styles.utrCounter} ${utr.length === 12 ? styles.utrCounterOk : ''}`}>
                                {utr.length}/12
                            </span>
                        </div>
                    </div>
                    {error && <div className={styles.errorText}><AlertTriangle size={11} />{error}</div>}
                </div>
                <div className={styles.modalFooter}>
                    <button
                        className={styles.btnPrimary}
                        onClick={handleSubmit}
                        disabled={isSubmitting || !isValid}
                    >
                        {isSubmitting
                            ? <><RefreshCw size={12} className={styles.spinIcon} /> Processing...</>
                            : <><CheckCircle size={12} /> Mark Processed</>}
                    </button>
                    <button className={styles.btnSecondary} onClick={onClose} disabled={isSubmitting}>Cancel</button>
                </div>
            </div>
        </div>
    )
}

// ─── UPI QR MODAL ─────────────────────────────────────────────────────────────
function UpiQrModal({ upiId, stylistName, onClose, amount = 100 }) {
    const upiUrl = `upi://pay?pa=${encodeURIComponent(upiId)}&pn=${encodeURIComponent(stylistName)}&am=${fmtAmount(amount)}&cu=INR&tn=${encodeURIComponent('For Stylelink Referral')}`
    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.qrModal} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>UPI QR Code</span>
                    <button className={styles.iconBtn} onClick={onClose}><X size={13} /></button>
                </div>
                <div className={styles.qrModalBody}>
                    <div className={styles.qrMeta}>
                        <span className={styles.qrStylistName}>{stylistName}</span>
                        <span className={styles.dot}>·</span>
                        <span className={styles.qrAmount}>{fmtAmount(amount)}</span>
                    </div>
                    <div className={styles.qrCodeWrapper}>
                        <QRCodeSVG value={upiUrl} size={200} level="M" />
                    </div>
                    <div className={styles.qrUpiId}>{upiId}</div>
                    <div className={styles.qrHint}>Scan to verify UPI ID · ₹{fmtAmount(amount)} fixed</div>
                </div>
            </div>
        </div>
    )
}

// ─── WITHDRAWAL ROW ───────────────────────────────────────────────────────────
function WithdrawalRow({ w, onApprove, onReject, onProcess }) {
    const isPending = w.status === 'submitted' || w.status === 'under_review'
    const isApproved = w.status === 'approved'
    const isProcessed = w.status === 'processed'
    const isRejected = w.status === 'rejected'
    const [showQr, setShowQr] = useState(false)

    return (
        <div className={`${styles.queueRow} ${w.isOldRequest ? styles.rowOld : ''}`}>
            <div className={styles.rowMain}>
                <div className={styles.rowLeft}>
                    <div className={styles.withdrawalId}>{w.withdrawalId}</div>
                    <div className={styles.stylistName}>{w.stylistName}</div>
                    <div className={styles.stylistMeta}>
                        <span>{w.stylistPhone}</span>
                        {w.stylistCity && (
                            <><span className={styles.dot}>·</span><span>{w.stylistCity}</span></>
                        )}
                    </div>
                    <div className={styles.metaLine}>
                        <Clock size={10} />
                        <span>{formatDateTime(w.submittedOn)}</span>
                        <span className={styles.dot}>·</span>
                        <span>Withdrawal #{w.withdrawalNumber}</span>
                        {w.upiId && (
                            <>
                                <span className={styles.dot}>·</span>
                                <span className={styles.upiMeta}>{w.upiId}</span>
                                <button className={styles.qrBtn} onClick={() => setShowQr(true)} title="Generate UPI QR">
                                    <QrCode size={11} />
                                </button>
                            </>
                        )}
                    </div>
                    {isProcessed && w.upiTransactionRef && (
                        <div className={styles.utrDisplay}>
                            UTR: <span className={styles.utrValue}>{w.upiTransactionRef}</span>
                        </div>
                    )}
                    {isRejected && w.rejectionReason && (
                        <div className={styles.rejectionDisplay}>
                            Reason: {w.rejectionReason}
                        </div>
                    )}
                </div>

                <div className={styles.rowRight}>
                    <div className={styles.amountBig}>{fmtAmount(w.amount)}</div>
                    <div className={styles.badgeRow}>
                        <StatusBadge status={w.status} />
                        {w.isOldRequest && (
                            <span className={styles.badge24hr}>
                                <AlertTriangle size={9} /> 24hr+
                            </span>
                        )}
                    </div>
                    {(isPending || isApproved) && (
                        <div className={styles.actionRow}>
                            {isPending && (
                                <button className={styles.btnApprove} onClick={onApprove}>
                                    <CheckCircle size={11} /> Approve
                                </button>
                            )}
                            {isApproved && (
                                <button className={styles.btnProcess} onClick={onProcess}>
                                    <CreditCard size={11} /> Mark Processed
                                </button>
                            )}
                            <button className={styles.btnReject} onClick={onReject}>
                                <XCircle size={11} /> Reject
                            </button>
                        </div>
                    )}
                </div>
            </div>
            {showQr && w.upiId && (
                <UpiQrModal
                    upiId={w.upiId}
                    stylistName={w.stylistName}
                    onClose={() => setShowQr(false)}
                    amount={w?.amount}
                />
            )}
        </div>
    )
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const WithdrawalQueueScreen = () => {
    const authCtx = useContext(AuthContext)
    const [withdrawals, setWithdrawals] = useState([])
    const [summary, setSummary] = useState(null)
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, hasMore: false })
    const [page, setPage] = useState(1)
    const [activeTab, setActiveTab] = useState('pending')
    const [isFetching, setIsFetching] = useState(false)
    const [lastUpdated, setLastUpdated] = useState(null)
    const [secondsAgo, setSecondsAgo] = useState(0)
    const [approveModal, setApproveModal] = useState(null)
    const [rejectModal, setRejectModal] = useState(null)
    const [processModal, setProcessModal] = useState(null)
    const [toast, setToast] = useState(null)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3500)
    }

    const fetchQueue = useCallback(async (tab, p) => {
        setIsFetching(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralGetWithdrawalQueue', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    adminId: authCtx.userIdentifier,
                    statusFilter: tab,
                    page: p,
                    limit: 20,
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                setWithdrawals(data.response?.withdrawals || [])
                setSummary(data.response?.summary || null)
                setPagination(data.response?.pagination || { page: 1, limit: 20, total: 0, hasMore: false })
                setLastUpdated(Date.now())
                setSecondsAgo(0)
            } else {
                if (data.message === 'token Expired, please login again!') authCtx.logout()
            }
        } catch (err) {
            console.error('fetchQueue error:', err)
        } finally {
            setIsFetching(false)
        }
    }, [authCtx.token, authCtx.userIdentifier])

    useEffect(() => {
        setPage(1)
        fetchQueue(activeTab, 1)
    }, [activeTab, fetchQueue])

    useEffect(() => {
        const id = setInterval(() => fetchQueue(activeTab, page), REFRESH_INTERVAL_MS)
        return () => clearInterval(id)
    }, [fetchQueue, activeTab, page])

    useEffect(() => {
        const id = setInterval(() => {
            if (lastUpdated) setSecondsAgo(Math.floor((Date.now() - lastUpdated) / 1000))
        }, 1000)
        return () => clearInterval(id)
    }, [lastUpdated])

    const handleActionDone = (msg, isError) => {
        setApproveModal(null)
        setRejectModal(null)
        setProcessModal(null)
        if (msg) showToast(msg, isError ? 'error' : 'success')
        fetchQueue(activeTab, page)
    }


    const isInitialLoad = isFetching && withdrawals.length === 0

    return (
        <div className={styles.mainWrapper}>
            {approveModal && (
                <ApproveModal
                    withdrawal={approveModal}
                    onClose={() => setApproveModal(null)}
                    onDone={handleActionDone}
                />
            )}
            {rejectModal && (
                <RejectModal
                    withdrawal={rejectModal}
                    onClose={() => setRejectModal(null)}
                    onDone={handleActionDone}
                />
            )}
            {processModal && (
                <ProcessModal
                    withdrawal={processModal}
                    onClose={() => setProcessModal(null)}
                    onDone={handleActionDone}
                />
            )}

            {toast && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
                    {toast.type === 'error' ? <AlertTriangle size={13} /> : <CheckCircle size={13} />}
                    {toast.message}
                </div>
            )}

            {/* ── HEADER ─────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <span className={styles.pageTitle}>Withdrawal Queue</span>
                    <div className={styles.refreshIndicator}>
                        {isFetching
                            ? <RefreshCw size={11} className={styles.spinIcon} />
                            : lastUpdated && <span className={styles.lastUpdatedText}>Updated {secondsAgo}s ago</span>
                        }
                    </div>
                </div>

                {summary && (
                    <div className={styles.summaryBar}>
                        <div className={styles.summaryChip}>
                            <span className={styles.summaryNum}>{summary.pendingCount}</span>
                            <span className={styles.summaryLabel}>Pending</span>
                        </div>
                        <div className={styles.summaryChip}>
                            <span className={styles.summaryNum}>{fmtAmount(summary.pendingAmount)}</span>
                            <span className={styles.summaryLabel}>Total</span>
                        </div>
                        <div className={styles.summaryChip}>
                            <CheckCircle size={11} color="#16a34a" />
                            <span className={styles.summaryNum}>{summary.processedToday}</span>
                            <span className={styles.summaryLabel}>Processed Today</span>
                        </div>
                    </div>
                )}
            </div>

            {/* ── TABS ───────────────────────────────────────────── */}
            <div className={styles.tabBar}>
                {TABS.map(tab => (
                    <button
                        key={tab.key}
                        className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                        onClick={() => { if (activeTab !== tab.key) setActiveTab(tab.key) }}
                    >
                        {tab.label}
                        {tab.key === 'pending' && summary?.pendingCount > 0 && (
                            <span className={styles.tabBadge}>{summary.pendingCount}</span>
                        )}
                    </button>
                ))}
            </div>

            {/* ── QUEUE BODY ──────────────────────────────────────── */}
            <div className={styles.queueBody}>
                {isInitialLoad ? (
                    <div className={styles.loadingState}>
                        <RefreshCw size={22} className={styles.spinIcon} />
                        <span>Loading withdrawals...</span>
                    </div>
                ) : withdrawals.length === 0 ? (
                    <div className={styles.emptyState}>
                        <CheckCircle size={40} color="#16a34a" />
                        <div className={styles.emptyTitle}>
                            {activeTab === 'pending'
                                ? 'Koi pending withdrawal nahi hai.'
                                : `No ${activeTab} withdrawals.`}
                        </div>
                    </div>
                ) : (
                    <div className={styles.queueList}>
                        {withdrawals.map(w => (
                            <WithdrawalRow
                                key={w.withdrawalId}
                                w={w}
                                onApprove={() => setApproveModal(w)}
                                onReject={() => setRejectModal(w)}
                                onProcess={() => setProcessModal(w)}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* ── PAGINATION ──────────────────────────────────────── */}
            {pagination.total > 0 && (
                <div className={styles.paginationBar}>
                    <span className={styles.paginationInfo}>
                        Showing {withdrawals.length} of {pagination.total}
                    </span>
                    <div className={styles.pageControls}>
                        <button
                            className={styles.pageBtn}
                            disabled={page <= 1}
                            onClick={() => setPage(p => p - 1)}
                        >
                            ← Prev
                        </button>
                        <span className={styles.pageNum}>
                            Page {pagination.page} of {pagination.totalPages || Math.ceil(pagination.total / pagination.limit) || 1}
                        </span>
                        <button
                            className={styles.pageBtn}
                            disabled={!pagination.hasMore}
                            onClick={() => setPage(p => p + 1)}
                        >
                            Next →
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default WithdrawalQueueScreen
